import React, { ChangeEvent, Component } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { ITestableComponentProps } from "@formant/core/model/ITestableComponentProps";
import { Field, ITextTypeFieldProps } from "@formant/ui/component/common/Field";
import styles from "@formant/ui/component/common/FloatingLabel.module.scss";

export interface INumberFieldProps
    extends
        ITestableComponentProps,
        Omit<ITextTypeFieldProps<HTMLInputElement>, "value"> {
    numberValue?: number;
    allowUndefined?: boolean;
    onChangeNumber?: (value: number | undefined) => void;
}

const decimalNumberRegex = /\-?\d*\.?\d*/;

@observer
export class NumberField extends Component<INumberFieldProps> {
    @observable
    private isFocused: boolean = false;
    @observable
    private stringValue?: string;
    @observable
    private modified = false;

    public componentDidUpdate(props: INumberFieldProps) {
        const { numberValue } = props;
        if (numberValue?.toString() === this.stringValue) {
            this.stringValue = undefined;
        }
    }

    public render() {
        const {
            allowUndefined,
            disabled,
            readOnly,
            numberValue,
            autoComplete,
            label,
            placeholder,
            autoFocus,
            className,
            small,
            onKeyDown,
        } = this.props;
        const { stringValue, isFocused } = this;

        const value =
            isFocused && stringValue !== undefined
                ? stringValue
                : numberValue !== undefined &&
                    (!allowUndefined || !this.modified)
                  ? numberValue.toString()
                  : "";

        return (
            <Field
                {...this.props}
                className={classnames(
                    className,
                    styles["floating-label-field"],
                    isFocused || (value && value.length > 0)
                        ? styles.focused
                        : null,
                    small ? styles.small : null,
                )}
                label={
                    isFocused ? (label ?? placeholder) : (placeholder ?? label)
                }
                filledOut={value !== undefined && value.length > 0}
            >
                <input
                    // text type is necessary so onChange is called
                    // for all input characters
                    type="text"
                    {...{
                        disabled,
                        readOnly,
                        value,
                        onChange: this.onChange,
                        onFocus: this.onInputFocus,
                        onBlur: this.onInputBlur,
                        autoComplete,
                        placeholder: undefined,
                        autoFocus,
                        onKeyDown,
                    }}
                    data-testid={this.props["data-testid"]}
                />
            </Field>
        );
    }

    private onChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { onChange, onChangeNumber } = this.props;

        this.modified = true;

        // trim leading zeroes
        const parsed = (
            decimalNumberRegex.exec(event.target.value)?.[0] || ""
        ).replace(/^(-?)0+(\d+)/, "$1$2");

        const numericValue = parseFloat(parsed);

        if (Number.isFinite(numericValue)) {
            // found a valid number within user input
            // update the input value with what we parsed
            onChangeNumber?.(numericValue);
        }
        // input value is not a valid number
        else if (parsed === "-" && !this.stringValue) {
            // input was empty, negative sign added
            // let user construct negative number
            // hack:
            // in order to keep input value a valid number
            // set input to negative zero
            onChangeNumber?.(0);
        } else if (parsed === "." && !this.stringValue) {
            // input was empty, decimal sign added
            // let user construct decimal number
            // hack:
            // in order to keep input value a valid number
            // set input to zero
            onChangeNumber?.(0);
        } else {
            // invalid/missing number entered
            // clear the input box
            onChangeNumber?.(undefined);
        }

        this.stringValue = event.target.value;

        onChange?.(event);
    };

    private onInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        const { onFocus } = this.props;
        this.isFocused = true;

        onFocus?.(event);
    };

    private onInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const { numberValue, onBlur } = this.props;

        if (numberValue !== undefined) {
            this.stringValue = numberValue.toLocaleString("fullwide", {
                useGrouping: false,
                maximumSignificantDigits: 20,
            });
        }

        if (event.target.value.length === 0) {
            this.isFocused = false;
        }

        onBlur?.(event);
    };
}
